import { ReferenceContactId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { handleEvent } from "util/user-events";
import { AiSummaryState } from "./ai-summary-state";
import { useEntityAiSummary } from "./use-entity-ai-summary";

export const useReferenceContactAiSummary = (
    referenceContactId: ReferenceContactId | null,
): AiSummaryState => {
    const trpcClient = trpc.useTRPC();

    const summaryQuery = useQuery({
        ...trpcClient.referenceContactAiSummary.find.queryOptions({
            entityId: referenceContactId!,
        }),
        enabled: referenceContactId !== null,
    });
    const generateMutation = useMutation({
        ...trpcClient.referenceContactAiSummary.generate.mutationOptions(),
    });
    const submitFeedbackMutation = useMutation({
        ...trpcClient.referenceContactAiSummary.submitFeedback.mutationOptions(),
    });
    const updateTextMutation = useMutation({
        ...trpcClient.referenceContactAiSummary.updateText.mutationOptions(),
    });
    const removeSummaryMutation = useMutation({
        ...trpcClient.referenceContactAiSummary.removeSummary.mutationOptions(),
    });

    return useEntityAiSummary({
        summary: summaryQuery.data,
        isQueryLoading: summaryQuery.isLoading,
        queryError: summaryQuery.error?.message,
        refetchSummary: () => summaryQuery.refetch(),

        triggerGenerate: () => {
            if (!referenceContactId) {
                return Promise.reject(
                    new Error(
                        "Cannot generate a reference contact summary without a reference contact id",
                    ),
                );
            }
            return generateMutation.mutateAsync({ entityId: referenceContactId });
        },
        isGenerating: generateMutation.isPending,
        generateError: generateMutation.error?.message,

        triggerSubmitFeedback: input => submitFeedbackMutation.mutate(input),
        isSubmittingFeedback: submitFeedbackMutation.isPending,
        submitFeedbackError: submitFeedbackMutation.error?.message,

        triggerUpdateText: input => updateTextMutation.mutateAsync(input),
        updateTextError: updateTextMutation.error?.message,

        triggerRemoveSummary: input => removeSummaryMutation.mutateAsync(input),
        isRemoving: removeSummaryMutation.isPending,
        removeSummaryError: removeSummaryMutation.error?.message,

        trackGenerate: () =>
            handleEvent("ai-summary-generate", {
                referenceContactId,
                isRegenerate: false,
            }),
        trackRegenerate: () =>
            handleEvent("ai-summary-generate", {
                referenceContactId,
                isRegenerate: true,
            }),
    });
};
